import RulerPicker from '@/components/RulerPicker/RulerPicker';
import { memo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import { useGetLeverage } from './hooks/useGetLeverage';
import { FormValue } from './type';
import { CloseIcon } from '@/icons';
import clsx from 'clsx';

type Props = { isOpen: boolean; onClose: () => void };

export const LeverageSheet = ({ isOpen, onClose }: Props) => {
    const { t } = useTranslation();
    const { setValue, control } = useFormContext<FormValue>();

    const [leverage, pairIndex] = useWatch({
        control,
        name: ['leverage', 'pairIndex'],
    });

    const { maxLeverage, minLeverage } = useGetLeverage(Number(pairIndex));

    const handleSave = useCallback(
        (leverage: number) => {
            console.debug('leverage', leverage);
            setValue('leverage', leverage);
            onClose();
        },
        [onClose, setValue]
    );

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content">
            <Sheet.Backdrop className="" onTap={onClose} />

            <Sheet.Container className=" p-4  mb-2 flex ">
                <Sheet.Content className=" justify-end z-50">
                    <div className="p-6 rounded-2xl shadow-lg ">
                        <div className="flex justify-between items-center">
                            <p className="text-lg font-bold text-slate-900">
                                {t('Leverage')}
                            </p>
                            <button onClick={onClose} className="p-2">
                                <CloseIcon className="text-[#1A1A1A]" />
                            </button>
                        </div>

                        <RulerPicker
                            max={maxLeverage}
                            min={minLeverage}
                            onSave={handleSave}
                            defaultValue={leverage}
                        />
                    </div>
                </Sheet.Content>
            </Sheet.Container>
        </Sheet>
    );
};

function Leverage() {
    const { control, setValue } = useFormContext<FormValue>();
    const { t } = useTranslation();

    const [isOpenSheet, setIsOpenSheet] = useState(false);

    const [leverage, pairIndex] = useWatch({
        control,
        name: ['leverage', 'pairIndex'],
    });
    const { leverageArray } = useGetLeverage(Number(pairIndex));

    const onChange = useCallback(
        (leverage: number) => {
            setValue('leverage', leverage);
        },
        [setValue]
    );

    return (
        <>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide ml-1">
                        Leverage
                    </label>
                    <p className="text-xl font-semibold text-slate-500 uppercase tracking-wide ml-1">
                        {leverage}x
                    </p>
                </div>
                <div
                    className={`flex flex-row grid-cols-${
                        leverageArray.length + 1
                    } gap-2 w-full flex-wrap`}
                >
                    {leverageArray.map((e) => {
                        return (
                            <button
                                onClick={() => {
                                    onChange(Number(e));
                                }}
                                className={clsx(
                                    'shadow-2xs border border-surface-light flex-1 min-w-10 h-9 w-full flex items-center justify-center rounded-lg text-slate-500 text-sm font-semibold hover:bg-slate-200 transition-colors',
                                    {
                                        ' bg-white':
                                            String(leverage) === String(e),
                                        ' bg-surface-light':
                                            String(leverage) !== String(e),
                                    }
                                )}
                            >
                                x{e}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => setIsOpenSheet(true)}
                        className={clsx(
                            'shadow-2xs border border-surface-light h-9 w-full flex items-center justify-center rounded-lg  text-slate-500 text-sm font-semibold hover:bg-slate-200 transition-colors',
                            {
                                ' bg-white': !leverageArray.includes(
                                    String(leverage)
                                ),
                                ' bg-surface-light': leverageArray.includes(
                                    String(leverage)
                                ),
                            }
                        )}
                    >
                        {t('Custom')}
                    </button>
                </div>
            </div>

            {isOpenSheet && (
                <LeverageSheet
                    isOpen={isOpenSheet}
                    onClose={() => setIsOpenSheet(false)}
                />
            )}
        </>
    );
}
export default memo(Leverage);
